<script setup lang="ts">
import * as R from 'ramda'
import { computed, reactive } from 'vue'

import {
  ManualIncidentEventStatusOption,
  ManualIncidentForm,
  ManualIncidentGroupData,
  ManualIncidentModal,
  ManualIncidentPayload,
} from '@collector/desktop-feature-incidents-ui'
import { Button } from '@collector/shared-ui'
import { Confirmation } from '@collector/sportsapi-client-legacy'
import {
  useParticipants,
  useSportMaps,
  useSwitchTeamColors,
} from '@desktop/views/Relation/composables'
import {
  useEventIncidentsQueue,
  useIncidentName,
} from '@desktop/views/Relation/Sports/composables'
import {
  injectEventClock,
  injectEventClockHelpers,
} from '@desktop/views/Relation/Sports/provide-inject'
import { injectIncidentsRequireConfirmation } from '@desktop/views/Relation/Sports/provide-inject/incidentsRequireConfirmation'
import { injectManualAddIncidentConfig } from '@desktop/views/Relation/Sports/provide-inject/manualAddIncidentConfig'

import IncidentButton from '../../../IncidentButton.vue'

type Validator = (value: unknown) => boolean

// inject
const manualAddIncidentConfig = injectManualAddIncidentConfig()
const incidentsRequireConfirmation = injectIncidentsRequireConfirmation()
const eventClock = injectEventClock()
const eventClockHelpers = injectEventClockHelpers()

// data
const minutesMaxDigits = eventClockHelpers?.minutesMaxDigits

// composables
const { isSwitchedTeamColors } = useSwitchTeamColors()
const { homeParticipant, awayParticipant } = useParticipants()
const eventIncidentsQueue = useEventIncidentsQueue()
const { eventStatusToSportStatusMap } = useSportMaps()
const { getIncidentName } = useIncidentName()

// data
const incidentGroups = computed<ManualIncidentGroupData[]>(() => [
  {
    title: 'Key incidents',
    incidents: manualAddIncidentConfig.getKeyIncidents(),
    layout: manualAddIncidentConfig.layouts?.keyIncidentsLayout,
  },
  {
    title: 'Other incidents',
    incidents: manualAddIncidentConfig.otherIncidents,
    layout: manualAddIncidentConfig.layouts?.otherIncidentsLayout,
  },
])

const eventStatuses = computed<ManualIncidentEventStatusOption[]>(() =>
  manualAddIncidentConfig.eventStatuses.map((eventStatusId) => ({
    id: eventStatusId,
    name: eventStatusToSportStatusMap.value[eventStatusId]?.name ?? '',
  })),
)

const eventStatusLayout = computed(() => {
  return manualAddIncidentConfig.layouts?.eventStatusesLayout
})

// state
const payload = reactive<ManualIncidentPayload>({
  minute: '',
  second: '',
  participant_team_id: null,
  incident_id: null,
  status_id: null,
})

// computed
const isSaveable = computed(() => {
  if (!eventClock) {
    return validatePayload([R.isNotNil])
  }

  const isTimeValid =
    payload.minute !== '' &&
    payload.second !== '' &&
    (payload.minute === 0 ? payload.second > 0 : payload.second >= 0) &&
    payload.second <= 59

  return validatePayload([R.isNotNil, R.isNotEmpty]) && isTimeValid
})

// methods
function validatePayload(validators: Validator[]): boolean {
  return R.values(payload).every(R.allPass(validators))
}

function addIncidentToQueue(): void {
  eventIncidentsQueue.addIncident({
    participant_team_id: payload.participant_team_id!,
    incident_id: payload.incident_id!,
    status_id: payload.status_id!,
    ...(eventClock && {
      minute: payload.minute as number,
      second: payload.second as number,
    }),
    ...(incidentsRequireConfirmation?.includes(payload.incident_id!) && {
      confirmation: Confirmation.ToBeDone,
    }),
  })
}
</script>

<template>
  <ManualIncidentModal
    title="Manual add incident"
    confirmText="Add incident"
    :confirmDisabled="!isSaveable"
    @confirm="addIncidentToQueue"
  >
    <template #trigger="{ open }">
      <Button
        class="bg-success-2 border-success-2 hover:border-success-4 hover:bg-success-4 text-neutral-10 w-full text-xs font-bold"
        icon="plus"
        size="sm"
        @click="open"
      >
        Manual add incident
      </Button>
    </template>

    <ManualIncidentForm
      v-model="payload"
      :homeParticipant
      :awayParticipant
      :isSwitchedTeamColors
      :incidentGroups
      :eventStatuses
      :eventStatusLayout
      :showClock="!!eventClock"
      :minutesMaxDigits
    >
      <template
        #incidentButton="{
          incidentId,
          disabled,
          selected,
          onClick,
          class: incidentButtonClass,
        }"
      >
        <IncidentButton
          :class="incidentButtonClass"
          :incidentId
          size="lg"
          :disabled
          :selected
          @click="onClick"
        >
          {{ getIncidentName(incidentId) }}
        </IncidentButton>
      </template>
    </ManualIncidentForm>
  </ManualIncidentModal>
</template>
